import { Stack } from '@mui/material'
import { decimalToLocalizedString, InfoTooltip, tokens } from 'ComponentLibrary'
import { TrendIcon } from 'ComponentLibrary/src/molecules/TrendIcon'
import { getTrendColor } from 'ComponentLibrary/src/common/colors'
import React, { ComponentProps, ReactNode, useId } from 'react'
import { Typography } from 'ComponentLibrary/src/piazza/text'

type KeyValueBoxProps = {
    label: string
    value?: number | string
    unit?: string
    trend?: number
    trendUnit?: string
    inverseTrendColor?: boolean
    valueColor?: string
    labelColor?: string
    prefix?: string
    info?: ReactNode
    size?: 'small' | 'medium' | 'large'
    dataTestId?: string
    locale?: string
}

export const KeyValueBox = ({
    label,
    value,
    unit,
    trend,
    trendUnit,
    inverseTrendColor,
    valueColor,
    labelColor,
    info,
    size = 'medium',
    prefix = '',
    dataTestId,
    locale = 'de-DE',
}: KeyValueBoxProps) => {
    const labelId = useId()

    const textVariants: Record<NonNullable<KeyValueBoxProps['size']>, ComponentProps<typeof Typography>['variant']> = {
        small: 'valueS',
        medium: 'valueM',
        large: 'valueL',
    }

    return (
        <Stack direction="column">
            <Stack direction="row" width="100%" justifyContent="space-between">
                <Typography variant="headline2" color={labelColor || tokens.ColorTextBoldest} id={labelId}>
                    {label}
                </Typography>
                {info !== undefined && <InfoTooltip text={info} />}
            </Stack>
            <Stack direction="row" alignItems="flex-end" justifyContent="space-between" gap={tokens.SizingSpacing050}>
                {trend !== undefined ? (
                    <TrendIcon variant="arrow" trend={trend} trendUnit={trendUnit} inverseColor={inverseTrendColor} locale={locale} />
                ) : (
                    <span />
                )}
                <Stack
                    role="group"
                    direction="row"
                    alignItems="flex-end"
                    height="48px"
                    whiteSpace="nowrap"
                    gap={tokens.SizingSpacing100}
                    data-testid={dataTestId}
                >
                    <Typography variant={textVariants[size]} color={getFormattedValueColor(valueColor, value)} aria-labelledby={labelId}>
                        {prefix + value}
                    </Typography>
                    <Typography variant="textS" color={tokens.ColorTextSubtle}>
                        {unit}
                    </Typography>
                </Stack>
            </Stack>
        </Stack>
    )
}

function getFormattedValueColor(customColor?: string, value?: KeyValueBoxProps['value']) {
    if (value === undefined) {
        return tokens.ColorTextSubtle
    }

    return customColor || tokens.ColorTextBoldest
}

type TrendKeyValueBoxProps = Pick<KeyValueBoxProps, 'label' | 'unit' | 'info' | 'locale'> & { value: number | undefined }

export const TrendKeyValueBox = ({ label, value, unit, info, locale = 'de-DE' }: TrendKeyValueBoxProps) => (
    <KeyValueBox
        size="small"
        value={value !== undefined ? decimalToLocalizedString(value, false, undefined, undefined, locale) : undefined}
        prefix={value ? getPrefix(value) : undefined}
        valueColor={getTrendColor(value)}
        label={label}
        unit={unit}
        info={info}
        locale={locale}
    />
)

function getPrefix(value: number) {
    if (value > 0) {
        return '+'
    }
    return ''
}
